import { useEffect, useRef, useState } from "react";
import { useDrinks, useStore } from "./hooks.ts";
import { APP, IS_PANTRY } from "./brand.ts";
import { Home } from "./views/Home.tsx";
import { Pantry } from "./views/Pantry.tsx";
import { InboundView } from "./views/Inbound.tsx";
import { update } from "./update.ts";
import { PRINT_PDF_EVENT, closePrintPdf, printPdf, sharePrintPdf } from "./print.ts";
import type { SignedIn } from "./account.ts";
import { Account } from "./views/Account.tsx";
import { Book } from "./views/Book.tsx";
import { Choose } from "./views/Choose.tsx";
import { Count } from "./views/Count.tsx";
import { ConfirmEmail, Join, Reset } from "./views/Links.tsx";
import { ConfirmBanner } from "./views/ConfirmBanner.tsx";
import { Login } from "./views/Login.tsx";
import { Handover, MovedBanner, TakeAlong, signedInHere } from "./views/Moved.tsx";
import { Measure } from "./views/Measure.tsx";
import { Setup } from "./views/Setup.tsx";
import { Signup } from "./views/Signup.tsx";
import { Status } from "./views/Status.tsx";
import { Stock } from "./views/Stock.tsx";
import { Wines } from "./views/Wines.tsx";
import { Places } from "./views/Places.tsx";
import { Overview } from "./views/Overview.tsx";
import { InventoryView } from "./views/Inventory.tsx";
import { Reports } from "./views/Reports.tsx";
import { Protocol } from "./views/Protocol.tsx";
import { Deadlines } from "./views/Deadlines.tsx";
import { useWide } from "./wide.ts";
import { useInventoryWatch } from "./inventoryWatch.ts";
import { unsaved } from "./unsaved.ts";

const CELLAR_TABS = [
  { route: "uebersicht", label: "Übersicht", wideOnly: true },
  { route: "buchen", label: "Buchen" },
  { route: "weine", label: "Weine" },
  { route: "bestand", label: "Bestand" },
  { route: "zaehlen", label: "Zählen" },
  { route: "konto", label: "Konto" },
] as const;
/** VorratsBuch (docs/18-vorratsbuch.md, V0-6): start with what expires and what to buy, no card, no day overview. */
const PANTRY_TABS = [
  { route: "start", label: "Start" },
  { route: "buchen", label: "Buchen" },
  { route: "vorraete", label: "Vorräte" },
  // Stock by storage (Vorratsschrank, Kühlschrank), with Fristen and Logbuch (Martin, 05.11.2026: „Bestand“ was unclear next to „Vorräte“).
  { route: "bestand", label: "Lager" },
  { route: "konto", label: "Konto" },
] as const;
const TABS: readonly { route: string; label: string; wideOnly?: boolean }[] = IS_PANTRY ? PANTRY_TABS : CELLAR_TABS;
const HOME = IS_PANTRY ? "start" : "buchen";

/** Views that use the whole width on a wide screen; the others stay a readable column. */
const WIDE_VIEWS = new Set(["uebersicht", "buchen", "weine", "vorraete", "bestand", "zaehlen", "inventur", "auswertung", "protokoll", "fristen"]);
/** Views without a tab of their own light up the tab they belong to. */
const TAB_OF: Record<string, string> = { inventur: "zaehlen", auswertung: "bestand", protokoll: "bestand", fristen: "bestand", eingang: "start" };

function useRoute(): string {
  const read = () => location.hash.replace(/^#\/?/, "").split("?")[0] || HOME;
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => setRoute(read());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

function SyncBadge() {
  const { store } = useStore();
  const pending = store.replica?.pending.length ?? 0;
  const { online, syncing, lastError } = store.status;
  let text = "aktuell";
  let cls = "ok";
  if (lastError) [text, cls] = [lastError, "error"];
  else if (!online) [text, cls] = [pending ? `offline · ${pending} wartend` : "offline", "offline"];
  else if (pending) [text, cls] = [`${pending} wartend`, "busy"];
  else if (syncing) [text, cls] = ["gleicht ab …", "busy"];
  return (
    <a className={`badge ${cls}`} href="#/status" data-testid="sync-badge">
      {text}
    </a>
  );
}

/**
 * Printing in the app on an iPhone's home screen: iOS does not print there, so the page becomes a
 * PDF, and the share sheet prints it (docs/15-getraenkekarte.md, Druck und Weg in die App).
 */
function PrintSheet() {
  const [, setTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    window.addEventListener(PRINT_PDF_EVENT, on);
    return () => window.removeEventListener(PRINT_PDF_EVENT, on);
  }, []);
  if (!printPdf.busy && !printPdf.file && !printPdf.error) return null;
  const close = () => {
    setError(null);
    closePrintPdf();
  };
  const share = () => sharePrintPdf().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  return (
    <div className="print-sheet" role="dialog" aria-label="Drucken" data-testid="print-sheet">
      {printPdf.busy && <p>PDF wird erstellt …</p>}
      {printPdf.file && (
        <>
          <p>
            <b>PDF ist fertig</b> ({printPdf.pages} {printPdf.pages === 1 ? "Seite" : "Seiten"}). Auf „Drucken …“ tippen, dann in der Liste <b>„Drucken“</b> wählen.
          </p>
          <button className="primary" onClick={share} data-testid="print-share">
            Drucken …
          </button>
        </>
      )}
      {(printPdf.error ?? error) && <p className="error">{printPdf.error ?? error}</p>}
      <button className="link" onClick={close}>
        {printPdf.file ? "Fertig" : "Abbrechen"}
      </button>
    </div>
  );
}

function UpdateBanner() {
  const [ready, setReady] = useState(update.ready);
  useEffect(() => {
    const on = () => setReady(true);
    window.addEventListener("lagerlog:update", on);
    return () => window.removeEventListener("lagerlog:update", on);
  }, []);
  if (!ready) return null;
  return (
    <div className="banner">
      Es gibt eine neue Version. <button onClick={() => update.apply()}>Neu laden</button>
    </div>
  );
}

/** The session ran out on a device with an account: its data stays, it only needs to sign in again. */
function SignInAgain() {
  const { store } = useStore();
  if (store.config?.token || store.status.lastError !== "Bitte neu anmelden") return null;
  return (
    <div className="banner">
      Die Anmeldung ist abgelaufen. Was du buchst, wartet auf dem Gerät. <a href="#/anmelden">Neu anmelden</a>
    </div>
  );
}

/** A member without any storage (F20): the owner grants them under Konto → Betrieb. */
function NothingGranted() {
  return (
    <section className="card" data-testid="nothing-granted">
      <h2>Hier ist nichts für dich freigegeben</h2>
      <p>Welche Lager du siehst und wo du buchen darfst, legt ein Inhaber unter Konto → Betrieb fest. Sobald ein Lager für dich freigegeben ist, erscheint es hier von selbst.</p>
    </section>
  );
}

export function App() {
  const { store } = useStore();
  const route = useRoute();
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  // A tap on the active tab „Vorräte“ shows its list again (B8): the view starts afresh.
  const [again, setAgain] = useState(0);
  const wide = useWide();
  // With a drink of a second profile the tab „Weine“ becomes „Getränke“ (docs/17-getraenke.md).
  const drinks = useDrinks();
  useInventoryWatch();
  // The header's height, for bars that stick right below it (SubNav).
  const header = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = header.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  });
  if (!store.ready) return <p className="loading">{APP.short} startet …</p>;
  if (route === "messung") return <Measure />;
  // Links from an invitation or a reset work whether or not someone is signed in on this device.
  if (!signedIn && route.startsWith("einladung/")) return <Join token={route.slice(10)} onSignedIn={setSignedIn} />;
  if (!signedIn && route.startsWith("passwort/")) return <Reset token={route.slice(9)} onSignedIn={setSignedIn} />;
  // The link to confirm an address works only signed in as its account; the page signs in first if needed.
  if (!signedIn && route.startsWith("email/")) return <ConfirmEmail key={route} token={route.slice(6)} onSignedIn={setSignedIn} />;
  // The move to mzapps.cloud: a sign-in carried over from the old address, and the way there to fetch it.
  if (!signedIn && route.startsWith("uebergabe/")) return <Handover key={route} handoverKey={route.slice(10)} onSignedIn={setSignedIn} />;
  if (!signedIn && route === "mitnehmen") return <TakeAlong onSignedIn={setSignedIn} />;
  if (signedIn) return <Choose signedIn={signedIn} key={signedIn.user.id} onDone={() => setSignedIn(null)} />;
  if (!store.config || route === "anmelden") {
    if (route === "zugang") return <Setup />;
    if (route === "registrieren") return <Signup onSignedIn={setSignedIn} />;
    return <Login onSignedIn={(s) => void signedInHere(s, setSignedIn)} />;
  }
  return (
    <div className="screen">
      <header ref={header}>
        <strong>{store.config.workspace}</strong>
        <SyncBadge />
      </header>
      <UpdateBanner />
      <MovedBanner />
      <PrintSheet />
      <SignInAgain />
      <ConfirmBanner route={route} />
      <main className={wide && WIDE_VIEWS.has(route) ? "wide" : ""}>
        {store.nothingGranted && route !== "konto" && route !== "status" ? <NothingGranted /> : route === "start" && IS_PANTRY ? <Home /> : route === "vorraete" && IS_PANTRY ? <Pantry key={again} /> : route === "eingang" && IS_PANTRY ? <InboundView /> : route === "uebersicht" ? <Overview /> : route === "inventur" ? <InventoryView /> : route === "auswertung" ? <Reports /> : route === "protokoll" ? <Protocol /> : route === "fristen" ? <Deadlines /> : route === "bestand" ? <Stock /> : route === "zaehlen" ? <Count /> : route === "status" ? <Status /> : route === "konto" ? <Account /> : route === "weine" ? <Wines /> : route === "lager" ? <Places /> : <Book />}
      </main>
      <nav>
        {TABS.filter((t) => wide || !t.wideOnly).map((t) => (
          <a
            key={t.route}
            href={`#/${t.route}`}
            onClick={(e) => {
              if (t.route !== route || t.route !== "vorraete") return;
              e.preventDefault();
              if (unsaved.dirty && !confirm("Änderungen verwerfen?")) return;
              unsaved.dirty = false;
              setAgain((n) => n + 1);
            }}
            className={(route === "uebersicht" && !wide ? "bestand" : (TAB_OF[route] ?? route)) === t.route ? "active" : ""}>
            {t.route === "weine" && drinks ? "Getränke" : t.label}
          </a>
        ))}
      </nav>
    </div>
  );
}
